// packages/decision-ui/src/components/DiscoveryHub/DiscoveryHub.tsx

import React, { useState, useEffect } from 'react';
import PhysicsGate from './PhysicsGate';
import TopologyMapper from './TopologyMapper';

export interface DiscoveryHubProps {
  onPhysicsUpdate?: (data: PhysicsData) => void;
  onTopologyUpdate?: (data: TopologyData) => void;
  initialMainsPressure?: number;
  initialFlowRate?: number;
  initialRadiatorCount?: number;
  initialPropertyFootprint?: number;
}

export interface PhysicsData {
  mainsPressure: number; // bar
  flowRate: number; // L/min
  combiViable: boolean;
  unventedViable: boolean;
}

export interface TopologyData {
  radiatorCount: number;
  propertyFootprint: number; // m²
  estimatedHeatLossKw: number;
  recommendedPlan: 'Y-Plan' | 'S-Plan' | 'W-Plan';
  planLabel: string;
  planRationale: string;
}

const DiscoveryHub: React.FC<DiscoveryHubProps> = ({
  onPhysicsUpdate,
  onTopologyUpdate,
  initialMainsPressure = 2.5,
  initialFlowRate = 18,
  initialRadiatorCount = 8,
  initialPropertyFootprint = 120,
}) => {
  const [physicsData, setPhysicsData] = useState<PhysicsData>({
    mainsPressure: initialMainsPressure,
    flowRate: initialFlowRate,
    combiViable: initialMainsPressure >= 1.0 && initialFlowRate >= 12,
    unventedViable: initialMainsPressure >= 1.0 && initialFlowRate >= 12,
  });

  const initialHeatLoss = calculateHeatLoss(initialRadiatorCount, initialPropertyFootprint);
  const initialPlan = determinePlan(initialHeatLoss, initialPropertyFootprint);
  
  const [topologyData, setTopologyData] = useState<TopologyData>({
    radiatorCount: initialRadiatorCount,
    propertyFootprint: initialPropertyFootprint,
    estimatedHeatLossKw: initialHeatLoss,
    recommendedPlan: initialPlan,
    planLabel: getPlanLabel(initialPlan),
    planRationale: getPlanRationale(initialPlan, initialHeatLoss, initialPropertyFootprint),
  });

  useEffect(() => {
    onPhysicsUpdate?.(physicsData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [physicsData]);

  useEffect(() => {
    onTopologyUpdate?.(topologyData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topologyData]);

  const handlePhysicsChange = (mainsPressure: number, flowRate: number) => {
    const combiViable = mainsPressure >= 1.0 && flowRate >= 12;
    const unventedViable = mainsPressure >= 1.0 && flowRate >= 12;

    const newData: PhysicsData = {
      mainsPressure,
      flowRate,
      combiViable,
      unventedViable,
    };

    setPhysicsData(newData);
    onPhysicsUpdate?.(newData);
  };

  const handleTopologyChange = (radiatorCount: number, propertyFootprint: number) => {
    const estimatedHeatLossKw = calculateHeatLoss(radiatorCount, propertyFootprint);
    const recommendedPlan = determinePlan(estimatedHeatLossKw, propertyFootprint);

    const newData: TopologyData = {
      radiatorCount,
      propertyFootprint,
      estimatedHeatLossKw,
      recommendedPlan,
      planLabel: getPlanLabel(recommendedPlan),
      planRationale: getPlanRationale(recommendedPlan, estimatedHeatLossKw, propertyFootprint),
    };

    setTopologyData(newData);
    onTopologyUpdate?.(newData);
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '2rem',
      padding: '2rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '12px',
    }}>
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: 700, margin: 0, marginBottom: '0.5rem' }}>
          Discovery Hub
        </h2>
        <p style={{ fontSize: '1rem', color: '#6c757d', margin: 0 }}>
          Let's discover what's physically possible for your property
        </p>
      </div>

      <PhysicsGate
        mainsPressure={physicsData.mainsPressure}
        flowRate={physicsData.flowRate}
        combiViable={physicsData.combiViable}
        unventedViable={physicsData.unventedViable}
        onChange={handlePhysicsChange}
      />

      <TopologyMapper
        radiatorCount={topologyData.radiatorCount}
        propertyFootprint={topologyData.propertyFootprint}
        estimatedHeatLossKw={topologyData.estimatedHeatLossKw}
        recommendedPlan={topologyData.recommendedPlan}
        planLabel={topologyData.planLabel}
        planRationale={topologyData.planRationale}
        onChange={handleTopologyChange}
      />
    </div>
  );
};

// Helper functions

function calculateHeatLoss(radiatorCount: number, propertyFootprint: number): number {
  // Rough estimate: ~100W per m² for moderate insulation
  // Or estimate based on radiator count (~1.5kW per radiator)
  const fromFootprint = (propertyFootprint * 100) / 1000; // kW
  const fromRadiators = radiatorCount * 1.5; // kW
  
  // Use average of both methods
  return Math.round((fromFootprint + fromRadiators) / 2 * 10) / 10;
}

function determinePlan(
  heatLossKw: number, 
  propertyFootprint: number
): 'Y-Plan' | 'S-Plan' | 'W-Plan' {
  // Y-Plan: Smaller systems (<19kW or <150m²)
  // S-Plan: Medium to large systems (19-35kW or 150-250m²)
  // W-Plan: Very large systems (>35kW or >250m²)
  
  if (heatLossKw < 19 || propertyFootprint < 150) {
    return 'Y-Plan';
  } else if (heatLossKw < 35 || propertyFootprint < 250) {
    return 'S-Plan';
  } else {
    return 'W-Plan';
  }
}

function getPlanLabel(plan: 'Y-Plan' | 'S-Plan' | 'W-Plan'): string {
  switch (plan) {
    case 'Y-Plan':
      return 'Streamlined Shared Junction';
    case 'S-Plan':
      return 'Advanced Multi-Zone';
    case 'W-Plan':
      return 'Premium Multi-Zone Plus';
    default:
      return 'Standard';
  }
}

function getPlanRationale(
  plan: 'Y-Plan' | 'S-Plan' | 'W-Plan',
  heatLossKw: number,
  propertyFootprint: number
): string {
  switch (plan) {
    case 'Y-Plan':
      return `With ${heatLossKw}kW heat loss and ${propertyFootprint}m², a Y-Plan offers the best value. On smaller systems, dual valves add complexity without meaningful zoning benefit.`;
    case 'S-Plan':
      return `With ${heatLossKw}kW heat loss and ${propertyFootprint}m², an S-Plan enables true independent zone control - heating and hot water operate separately for optimal efficiency.`;
    case 'W-Plan':
      return `With ${heatLossKw}kW heat loss and ${propertyFootprint}m², a W-Plan provides maximum flexibility with multiple independent heating zones plus hot water priority.`;
    default:
      return '';
  }
}

export default DiscoveryHub;
